import { AxTabbedBase } from '@theme/ax-tabbed-base';

/**
 * Tabbed accordion blocks — side image + accordion list per tab.
 *
 * Extends AxTabbedBase for top-level tab switching (Section Rendering API,
 * tablist ARIA sync, keyboard nav, indicator). Adds:
 *   - Per-panel image swap when an accordion opens (cloned from a <template>)
 *   - Revert to default picture when all accordions in a panel are closed
 *
 * Accordions are grouped via <details name="..."> so only one is open at a
 * time per panel — keeps the image-swap UX coherent.
 *
 * @extends {AxTabbedBase}
 */
class AxTabbedAccordionBlocksComponent extends AxTabbedBase {
  static bemPrefix = 'ax-tabbed-accordion-blocks';

  #onToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    if (!details.classList.contains('ax-tabbed-accordion-blocks__details')) return;

    const panel = details.closest('.ax-tabbed-accordion-blocks__panel');
    if (!panel) return;

    if (details.open) {
      const template = details.querySelector(':scope > template[data-row-picture]');
      if (template) this.#swapPicture(panel, template);
      return;
    }

    // Closed — if another accordion in the same panel is still open, show its image
    const openSibling = panel.querySelector('details.ax-tabbed-accordion-blocks__details[open]');
    if (openSibling) {
      const template = openSibling.querySelector(':scope > template[data-row-picture]');
      if (template) {
        this.#swapPicture(panel, template);
        return;
      }
    }
    this.#restoreDefaultPicture(panel);
  };

  connectedCallback() {
    super.connectedCallback();
    // toggle does not bubble — listen at capture to catch every accordion
    this.addEventListener('toggle', this.#onToggle, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('toggle', this.#onToggle, { capture: true });
  }

  /**
   * Replace the panel's media slot content with the picture cloned from a
   * row's <template data-row-picture>. Hides every existing media child
   * (picture OR video) so it can be restored on close — covers both the
   * "tab has image" and "tab has video" cases.
   *
   * @param {HTMLElement} panel
   * @param {HTMLTemplateElement} template
   */
  #swapPicture(panel, template) {
    const mediaSlot = panel.querySelector('.ax-tabbed-accordion-blocks__media');
    if (!mediaSlot) return;

    mediaSlot.dataset.swapping = 'true';

    requestAnimationFrame(() => {
      const previousSwap = mediaSlot.querySelector('picture[data-row-swap]');
      previousSwap?.remove();

      for (const child of mediaSlot.children) {
        if (child.hasAttribute('data-row-swap')) continue;
        child.hidden = true;
      }

      const fragment = template.content.cloneNode(true);
      const newPicture = fragment.querySelector('picture');
      if (newPicture) {
        newPicture.dataset.rowSwap = 'true';
        mediaSlot.appendChild(newPicture);
      }

      delete mediaSlot.dataset.swapping;
    });
  }

  /**
   * @param {HTMLElement} panel
   */
  #restoreDefaultPicture(panel) {
    const mediaSlot = panel.querySelector('.ax-tabbed-accordion-blocks__media');
    if (!mediaSlot) return;

    mediaSlot.dataset.swapping = 'true';

    requestAnimationFrame(() => {
      const swapped = mediaSlot.querySelector('picture[data-row-swap]');
      swapped?.remove();

      for (const child of mediaSlot.children) {
        child.hidden = false;
      }

      delete mediaSlot.dataset.swapping;
    });
  }
}

customElements.define('ax-tabbed-accordion-blocks-component', AxTabbedAccordionBlocksComponent);
